import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Fab,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  FormControlLabel,
  Switch,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  Image as ImageIcon,
  AddPhotoAlternate as AddPhotoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  FilterList as FilterIcon,
  CalendarMonth as CalendarIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { expenseService, expenseCategoryService, eventVehicleUsageService } from '../services/api';
import { Expense, ExpenseCategory, EventVehicleUsage } from '../types';
import { divertilandiaColors } from '../theme/divertilandiaColors';
import ImageUpload from '../components/ImageUpload';

const Expenses: React.FC = () => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [vehicleUsages, setVehicleUsages] = useState<EventVehicleUsage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [formData, setFormData] = useState({
    concept: '',
    description: '',
    amount: 0,
    receipt_image: '',
    expense_category_id: '',
    status: 'pending' as 'pending' | 'paid',
    expense_date: new Date().toISOString().split('T')[0],
    payment_date: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [expensesData, categoriesData, vehicleUsagesData] = await Promise.all([
        expenseService.getAll(),
        expenseCategoryService.getAll({ active: true }), // Solo categorías activas
        eventVehicleUsageService.getAll()
      ]);
      setExpenses(expensesData);
      setCategories(categoriesData);
      setVehicleUsages(vehicleUsagesData);
    } catch (err) {
      setError('Error al cargar los datos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (expense?: Expense) => {
    if (expense) {
      setEditingExpense(expense);
      setFormData({
        concept: expense.concept,
        description: expense.description || '',
        amount: typeof expense.amount === 'string' ? parseFloat(expense.amount) || 0 : expense.amount || 0,
        receipt_image: expense.receipt_image || '',
        expense_category_id: expense.expense_category_id,
        status: expense.status,
        expense_date: expense.expense_date,
        payment_date: expense.payment_date || '',
      });
    } else {
      setEditingExpense(null);
      setFormData({
        concept: '',
        description: '',
        amount: 0,
        receipt_image: '',
        expense_category_id: '',
        status: 'pending',
        expense_date: new Date().toISOString().split('T')[0],
        payment_date: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingExpense(null);
    setError(null); // Limpiar errores al cerrar el diálogo
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Filtrar gastos por mes (incluyendo gasolina)
  const getFilteredExpenses = () => {
    const [year, month] = selectedMonth.split('-');
    return expenses.filter(expense => {
      const expenseDate = new Date(expense.expense_date);
      const expenseYear = expenseDate.getFullYear().toString();
      const expenseMonth = String(expenseDate.getMonth() + 1).padStart(2, '0');
      
      return expenseYear === year && expenseMonth === month;
    });
  };

  // Calcular totales del mes
  const getMonthlyTotals = () => {
    const filteredExpenses = getFilteredExpenses();
    const total = filteredExpenses.reduce((sum, expense) => {
      const amount = typeof expense.amount === 'string' ? parseFloat(expense.amount) || 0 : expense.amount || 0;
      return sum + amount;
    }, 0);
    
    const paid = filteredExpenses
      .filter(expense => expense.status === 'paid')
      .reduce((sum, expense) => {
        const amount = typeof expense.amount === 'string' ? parseFloat(expense.amount) || 0 : expense.amount || 0;
        return sum + amount;
      }, 0);
    
    const pending = total - paid;
    
    return { total, paid, pending, count: filteredExpenses.length };
  };

  // Formatear nombre del mes
  const getMonthName = (monthString: string) => {
    const [year, month] = monthString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('es-ES', { year: 'numeric', month: 'long' });
  };

  // Navegar entre meses
  const navigateMonth = (direction: 'prev' | 'next') => {
    const [year, month] = selectedMonth.split('-');
    const currentDate = new Date(parseInt(year), parseInt(month) - 1);
    
    if (direction === 'prev') {
      currentDate.setMonth(currentDate.getMonth() - 1);
    } else {
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
    
    const newYear = currentDate.getFullYear();
    const newMonth = String(currentDate.getMonth() + 1).padStart(2, '0');
    setSelectedMonth(`${newYear}-${newMonth}`);
  };

  const handleSave = async () => {
    try {
      // Validar campos requeridos
      if (!formData.concept.trim()) {
        setError('El concepto es requerido');
        return;
      }
      
      if (!formData.expense_category_id) {
        setError('La categoría es requerida');
        return;
      }
      
      if (formData.amount <= 0) {
        setError('El monto debe ser mayor a 0');
        return;
      }
      
      if (!formData.expense_date) {
        setError('La fecha del gasto es requerida');
        return;
      }

      if (editingExpense) {
        await expenseService.update(editingExpense.id, formData);
      } else {
        await expenseService.create(formData);
      }
      fetchData();
      handleCloseDialog();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Error al guardar el gasto';
      setError(errorMessage);
      console.error('Error completo:', err.response?.data || err);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este gasto?')) {
      try {
        await expenseService.delete(id);
        fetchData();
      } catch (err) {
        setError('Error al eliminar el gasto');
        console.error(err);
      }
    }
  };

  const handleMarkAsPaid = async (id: string) => {
    try {
      // Marcar como pagado en gastos regulares
      await expenseService.markAsPaid(id);
      
      // Buscar si este gasto está relacionado con un uso de vehículo (gasolina)
      const expense = expenses.find(e => e.id === id);
      if (expense) {
        // Verificar si es un gasto de gasolina por el concepto o categoría
        const isGasolineExpense = expense.category?.name?.toLowerCase().includes('gasolina') || 
                                 expense.concept?.toLowerCase().includes('gasolina');
        
        if (isGasolineExpense) {
          console.log('Gasto de gasolina detectado:', expense);
          console.log('Buscando en vehicleUsages:', vehicleUsages.length, 'usos');
          
          // Buscar el uso de vehículo relacionado por el monto y fecha
          const relatedVehicleUsage = vehicleUsages.find(usage => {
            const usageAmount = typeof usage.fuel_cost === 'string' ? parseFloat(usage.fuel_cost) || 0 : usage.fuel_cost || 0;
            const expenseAmount = typeof expense.amount === 'string' ? parseFloat(expense.amount) || 0 : expense.amount || 0;
            
            // Crear fechas para comparación
            const usageDate = new Date(usage.created_at);
            const expenseDate = new Date(expense.expense_date);
            
            // Comparar solo el día, mes y año (ignorar hora)
            const usageDateOnly = new Date(usageDate.getFullYear(), usageDate.getMonth(), usageDate.getDate());
            const expenseDateOnly = new Date(expenseDate.getFullYear(), expenseDate.getMonth(), expenseDate.getDate());
            
            const amountMatch = Math.abs(usageAmount - expenseAmount) < 0.01;
            
            // Calcular diferencia en días
            const timeDiff = Math.abs(usageDateOnly.getTime() - expenseDateOnly.getTime());
            const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
            
            // Permitir diferencia de hasta 30 días (más flexible)
            const dateMatch = daysDiff <= 30;
            
            console.log('Comparando:', {
              usageAmount,
              expenseAmount,
              usageDate: usageDateOnly.toDateString(),
              expenseDate: expenseDateOnly.toDateString(),
              amountMatch,
              daysDiff,
              dateMatch,
              usageId: usage.id,
              expenseId: expense.id
            });
            
            return amountMatch && dateMatch;
          });
          
          console.log('Uso de vehículo relacionado encontrado:', relatedVehicleUsage);
          
          if (relatedVehicleUsage) {
            // Marcar como pagado también en event-vehicle-usage
            await eventVehicleUsageService.markAsPaid(relatedVehicleUsage.id);
            console.log('Sincronización exitosa con event-vehicle-usage');
          } else {
            console.log('No se encontró uso de vehículo relacionado');
          }
        }
      }
      
      fetchData();
    } catch (err) {
      setError('Error al marcar como pagado');
      console.error(err);
    }
  };

  const getStatusColor = (status: string) => {
    return status === 'paid' ? divertilandiaColors.rainbow.green : divertilandiaColors.rainbow.orange;
  };

  const getStatusIcon = (status: string) => {
    return status === 'paid' ? <CheckCircleIcon /> : <PendingIcon />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" sx={{ color: divertilandiaColors.standard.primary }}>
          Gestión de Gastos
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ backgroundColor: divertilandiaColors.rainbow.violet }}
        >
          Agregar Gasto
        </Button>
      </Box>

      {/* Filtro por mes */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
          <FilterIcon sx={{ mr: 1, color: divertilandiaColors.standard.primary }} />
          <Typography variant="h6">Filtrar por Mes</Typography>
        </Box>
        
        {/* Selector de mes con navegación */}
        <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ChevronLeftIcon />}
            onClick={() => navigateMonth('prev')}
            sx={{ minWidth: 'auto', px: 2 }}
          >
            Anterior
          </Button>
          
          <Paper 
            elevation={2} 
            sx={{ 
              px: 3, 
              py: 1.5, 
              backgroundColor: divertilandiaColors.standard.primary,
              color: 'white',
              minWidth: 200,
              textAlign: 'center'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {getMonthName(selectedMonth)}
            </Typography>
          </Paper>
          
          <Button
            variant="outlined"
            endIcon={<ChevronRightIcon />}
            onClick={() => navigateMonth('next')}
            sx={{ minWidth: 'auto', px: 2 }}
          >
            Siguiente
          </Button>
        </Box>
        
        <Typography variant="body2" color="text.secondary">
          Mostrando todos los gastos de {getMonthName(selectedMonth)}
        </Typography>
      </Box>

      {/* Tarjetas de resumen del mes */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                <ReceiptIcon sx={{ mr: 1, color: divertilandiaColors.rainbow.blue }} />
                <Typography variant="h6">Total Gastos</Typography>
              </Box>
              <Typography variant="h4" sx={{ color: divertilandiaColors.rainbow.blue, fontWeight: 'bold' }}>
                {getMonthlyTotals().count}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                <MoneyIcon sx={{ mr: 1, color: divertilandiaColors.rainbow.green }} />
                <Typography variant="h6">Total Monto</Typography>
              </Box>
              <Typography variant="h4" sx={{ color: divertilandiaColors.rainbow.green, fontWeight: 'bold' }}>
                ${getMonthlyTotals().total.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                <CheckCircleIcon sx={{ mr: 1, color: divertilandiaColors.rainbow.orange }} />
                <Typography variant="h6">Pagados</Typography>
              </Box>
              <Typography variant="h4" sx={{ color: divertilandiaColors.rainbow.orange, fontWeight: 'bold' }}>
                ${getMonthlyTotals().paid.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                <PendingIcon sx={{ mr: 1, color: divertilandiaColors.rainbow.red }} />
                <Typography variant="h6">Pendientes</Typography>
              </Box>
              <Typography variant="h4" sx={{ color: divertilandiaColors.rainbow.red, fontWeight: 'bold' }}>
                ${getMonthlyTotals().pending.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {getFilteredExpenses().length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <ReceiptIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No hay gastos registrados para {getMonthName(selectedMonth)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Usa el botón "Agregar Gasto" para registrar nuevos gastos
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {getFilteredExpenses().map((expense) => (
          <Grid item xs={12} md={6} lg={4} key={expense.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                    {expense.concept}
                  </Typography>
                  <Chip
                    icon={getStatusIcon(expense.status)}
                    label={expense.status === 'paid' ? 'Pagado' : 'Pendiente'}
                    sx={{
                      backgroundColor: getStatusColor(expense.status),
                      color: 'white',
                      fontWeight: 600,
                    }}
                  />
                </Box>

                {expense.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {expense.description}
                  </Typography>
                )}

                <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                  <MoneyIcon sx={{ mr: 1, color: divertilandiaColors.rainbow.green }} />
                  <Typography variant="h5" sx={{ color: divertilandiaColors.rainbow.green, fontWeight: 'bold' }}>
                    ${(typeof expense.amount === 'string' ? parseFloat(expense.amount) || 0 : expense.amount || 0).toFixed(2)}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                  <ReceiptIcon sx={{ mr: 1, color: expense.category?.color || divertilandiaColors.standard.primary }} />
                  <Typography variant="body2" sx={{ color: expense.category?.color || divertilandiaColors.standard.primary, fontWeight: 600 }}>
                    {expense.category?.name}
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Fecha: {(() => {
                    // Crear fecha local sin problemas de zona horaria
                    const [year, month, day] = expense.expense_date.split('T')[0].split('-');
                    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                    return date.toLocaleDateString('es-ES');
                  })()}
                </Typography>

                {expense.receipt_image && (
                  <Box sx={{ mb: 2 }}>
                    <img
                      src={expense.receipt_image.startsWith('data:') ? expense.receipt_image : `data:image/jpeg;base64,${expense.receipt_image}`}
                      alt="Recibo"
                      style={{
                        width: '100%',
                        maxHeight: '200px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                      }}
                    />
                  </Box>
                )}

                <Box display="flex" gap={1} mt={2}>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenDialog(expense)}
                    sx={{ flex: 1 }}
                  >
                    Editar
                  </Button>
                  {expense.status === 'pending' && (
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={() => handleMarkAsPaid(expense.id)}
                      sx={{ flex: 1 }}
                    >
                      Marcar Pagado
                    </Button>
                  )}
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(expense.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
        </Grid>
      )}

      {/* Dialog para agregar/editar gasto */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingExpense ? 'Editar Gasto' : 'Agregar Nuevo Gasto'}
        </DialogTitle>
        <DialogContent>
          {error && openDialog && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Imagen del Recibo/Ticket (Opcional)
              </Typography>
              <ImageUpload
                value={formData.receipt_image}
                onChange={(value) => handleInputChange('receipt_image', value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Concepto *"
                value={formData.concept}
                onChange={(e) => handleInputChange('concept', e.target.value)}
                required
                error={!formData.concept.trim()}
                helperText={!formData.concept.trim() ? 'Este campo es requerido' : ''}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required error={!formData.expense_category_id}>
                <InputLabel>Categoría *</InputLabel>
                <Select
                  value={formData.expense_category_id}
                  onChange={(e) => handleInputChange('expense_category_id', e.target.value)}
                  label="Categoría *"
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      <Box display="flex" alignItems="center">
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            backgroundColor: category.color,
                            borderRadius: '50%',
                            mr: 1,
                          }}
                        />
                        {category.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {!formData.expense_category_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    Este campo es requerido
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Monto *"
                type="number"
                value={formData.amount}
                onChange={(e) => handleInputChange('amount', parseFloat(e.target.value) || 0)}
                inputProps={{ min: 0.01, step: 0.01 }}
                required
                error={formData.amount <= 0}
                helperText={formData.amount <= 0 ? 'El monto debe ser mayor a 0' : ''}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Fecha del Gasto *"
                type="date"
                value={formData.expense_date}
                onChange={(e) => handleInputChange('expense_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                error={!formData.expense_date}
                helperText={!formData.expense_date ? 'Este campo es requerido' : ''}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción (Opcional)"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                multiline
                rows={3}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  label="Estado"
                >
                  <MenuItem value="pending">Pendiente</MenuItem>
                  <MenuItem value="paid">Pagado</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {formData.status === 'paid' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Fecha de Pago (Opcional)"
                  type="date"
                  value={formData.payment_date}
                  onChange={(e) => handleInputChange('payment_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained">
            {editingExpense ? 'Actualizar' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Expenses;
